export const SCHEDULE_INTERVAL_MINUTES = 30

export const WEEKDAY_KEYS = ['0', '1', '2', '3', '4', '5', '6']

export const ALL_SLOT_TIMES = buildRangeSlots('07:00', '22:00')

function buildRangeSlots (from, to) {
  const start = toMinutes(from)
  const end = toMinutes(to)
  const out = []
  for (let m = start; m <= end; m += SCHEDULE_INTERVAL_MINUTES) {
    out.push(fromMinutes(m))
  }
  return out
}

function toMinutes (time) {
  const [h, min] = time.split(':').map(Number)
  return h * 60 + min
}

function fromMinutes (total) {
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function defaultSlotsForDay (dayKey) {
  const d = Number(dayKey)
  if (d === 0) return []
  if (d === 6) return buildRangeSlots('08:00', '12:00')
  return buildRangeSlots('08:00', '18:00')
}

function normalizeSlotList (list) {
  const valid = new Set(ALL_SLOT_TIMES)
  const out = new Set()
  for (const item of list || []) {
    const slot = String(item || '').trim()
    if (valid.has(slot)) out.add(slot)
  }
  return [...out].sort()
}

export function defaultScheduleConfig () {
  const horariosPorDia = {}
  for (const key of WEEKDAY_KEYS) {
    horariosPorDia[key] = defaultSlotsForDay(key)
  }
  return { intervaloMinutos: SCHEDULE_INTERVAL_MINUTES, horariosPorDia }
}

export function normalizeScheduleConfig (raw) {
  const base = defaultScheduleConfig()
  const out = {
    intervaloMinutos: raw?.intervaloMinutos || SCHEDULE_INTERVAL_MINUTES,
    horariosPorDia: { ...base.horariosPorDia }
  }
  const src = raw?.horariosPorDia || {}
  for (const key of WEEKDAY_KEYS) {
    if (Array.isArray(src[key])) {
      out.horariosPorDia[key] = normalizeSlotList(src[key])
    } else if (raw?.horariosPorDia) {
      out.horariosPorDia[key] = []
    }
  }
  return out
}

export function hasAnyScheduleSlot (config) {
  const c = normalizeScheduleConfig(config)
  return WEEKDAY_KEYS.some(k => c.horariosPorDia[k]?.length > 0)
}

export function isSlotEnabled (config, dayKey, slot) {
  return normalizeScheduleConfig(config).horariosPorDia[dayKey]?.includes(slot)
}

export function toggleScheduleSlot (config, dayKey, slot) {
  const c = normalizeScheduleConfig(config)
  const list = [...(c.horariosPorDia[dayKey] || [])]
  const idx = list.indexOf(slot)
  if (idx >= 0) list.splice(idx, 1)
  else list.push(slot)
  c.horariosPorDia[dayKey] = normalizeSlotList(list)
  return c
}

export function setAllSlotsForDay (config, dayKey, enabled) {
  const c = normalizeScheduleConfig(config)
  c.horariosPorDia[dayKey] = enabled ? [...ALL_SLOT_TIMES] : []
  return c
}

export function copyWeekdaysFrom (config, sourceDayKey) {
  const c = normalizeScheduleConfig(config)
  const source = [...(c.horariosPorDia[sourceDayKey] || [])]
  for (const key of ['1', '2', '3', '4', '5']) {
    c.horariosPorDia[key] = [...source]
  }
  return c
}

export function slotIndex (slot) {
  return ALL_SLOT_TIMES.indexOf(slot)
}

export function applySlotRange (config, dayKey, fromSlot, toSlot, mode = 'add') {
  const c = normalizeScheduleConfig(config)
  const a = slotIndex(fromSlot)
  const b = slotIndex(toSlot)
  if (a < 0 || b < 0) return c
  const from = Math.min(a, b)
  const to = Math.max(a, b)
  const set = new Set(c.horariosPorDia[dayKey] || [])
  for (let i = from; i <= to; i++) {
    const slot = ALL_SLOT_TIMES[i]
    if (mode === 'remove') set.delete(slot)
    else set.add(slot)
  }
  c.horariosPorDia[dayKey] = normalizeSlotList([...set])
  return c
}

export function slotsToRanges (slots) {
  const sorted = normalizeSlotList(slots)
  if (!sorted.length) return []
  const ranges = []
  let start = sorted[0]
  let prev = sorted[0]
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i]
    if (slotIndex(cur) === slotIndex(prev) + 1) {
      prev = cur
      continue
    }
    ranges.push({ from: start, to: prev })
    start = cur
    prev = cur
  }
  ranges.push({ from: start, to: prev })
  return ranges
}

export function formatDayRangesLabel (slots) {
  const ranges = slotsToRanges(slots)
  if (!ranges.length) return ''
  return ranges.map(r => (r.from === r.to ? r.from : `${r.from}–${r.to}`)).join(', ')
}

export function formatScheduleSummary (config, dayLabels) {
  const c = normalizeScheduleConfig(config)
  const parts = []
  for (const key of WEEKDAY_KEYS) {
    const slots = c.horariosPorDia[key] || []
    if (!slots.length) continue
    const label = dayLabels?.[key] || key
    parts.push(`${label} ${formatDayRangesLabel(slots)}`)
  }
  return parts.length ? parts.join(' · ') : ''
}

export function dayHasConfiguredSlots (config, dayKey) {
  return (normalizeScheduleConfig(config).horariosPorDia[dayKey] || []).length > 0
}

export function jsDayToConfigKey (date) {
  return String(date.getDay())
}
